import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, formatApiErrorDetail, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { RoleBadge } from "@/components/Shared";
import { Shield, Ban, RotateCcw, PencilLine, PowerOff, PlusCircle, Search, X, Download, FileText } from "lucide-react";

async function callApi(fn) {
  try { const r = await fn(); return r; }
  catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); return null; }
}

export default function AdminPanel({ room, onChange }) {
  const [open, setOpen] = useState(false);
  const [rosterOpen, setRosterOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [role, setRole] = useState("P");
  const [search, setSearch] = useState("");
  const [players, setPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [priceInput, setPriceInput] = useState(1);

  useEffect(() => {
    if (!addOpen) return;
    api.get(`/players`, { params: { role, search: search || undefined } })
      .then((r) => setPlayers(r.data)).catch(() => {});
  }, [role, search, addOpen]);

  const ROLE_ORDER = ["P", "D", "C", "A"];
  const availablePlayers = useMemo(
    () => players.filter(p => !room.sold_player_ids.includes(p.id)),
    [players, room.sold_player_ids]
  );

  const cancelBid = async () => {
    const r = await callApi(() => api.post(`/rooms/${room.id}/admin/cancel-bid`));
    if (r) { toast.success("Offerta annullata"); onChange?.(r.data); }
  };
  const cancelAuction = async () => {
    const r = await callApi(() => api.post(`/rooms/${room.id}/admin/cancel-auction`));
    if (r) { toast.success("Asta annullata"); onChange?.(r.data); }
  };
  const undoLast = async () => {
    const r = await callApi(() => api.post(`/rooms/${room.id}/admin/undo-last-assignment`));
    if (r) { toast.success("Aggiudicazione annullata, crediti restituiti"); onChange?.(r.data); }
  };
  const finishLeague = async () => {
    if (!confirm("Chiudere la lega e generare l'export? Potrai comunque riaprirla.")) return;
    const r = await callApi(() => api.post(`/rooms/${room.id}/admin/finish`));
    if (r) { toast.success("Lega chiusa"); onChange?.(r.data); setOpen(false); }
  };
  const reopen = async () => {
    const r = await callApi(() => api.post(`/rooms/${room.id}/admin/reopen`));
    if (r) { toast.success("Lega riaperta"); onChange?.(r.data); }
  };

  const removePlayer = async (teamId, playerId) => {
    const r = await callApi(() => api.post(`/rooms/${room.id}/admin/edit-roster`, {
      team_id: teamId, action: "remove", player_id: playerId,
    }));
    if (r) { toast.success("Calciatore rimosso, crediti restituiti"); onChange?.(r.data); }
  };
  const updatePrice = async (teamId, playerId, newPrice) => {
    const r = await callApi(() => api.post(`/rooms/${room.id}/admin/edit-roster`, {
      team_id: teamId, action: "update_price", player_id: playerId, price: Number(newPrice),
    }));
    if (r) { toast.success("Prezzo aggiornato"); onChange?.(r.data); }
  };
  const addPlayer = async () => {
    if (!selectedPlayer || !selectedTeamId) return;
    const r = await callApi(() => api.post(`/rooms/${room.id}/admin/edit-roster`, {
      team_id: selectedTeamId, action: "add", player_id: selectedPlayer.id, price: Number(priceInput),
    }));
    if (r) {
      toast.success("Calciatore aggiunto alla rosa");
      onChange?.(r.data);
      setAddOpen(false); setSelectedPlayer(null); setPriceInput(1);
    }
  };
  const editCredits = async (teamId, credits) => {
    const r = await callApi(() => api.post(`/rooms/${room.id}/admin/edit-credits`, {
      team_id: teamId, credits: Number(credits),
    }));
    if (r) { toast.success("Crediti aggiornati"); onChange?.(r.data); }
  };

  const downloadExport = (kind) => {
    const url = `${API_BASE}/rooms/${room.id}/export.${kind}`;
    window.open(url, "_blank");
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button data-testid="admin-panel-btn" size="sm" variant="outline" className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300">
            <Shield className="w-4 h-4 mr-1" />Admin
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl bg-slate-950 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black flex items-center gap-2">
              <Shield className="w-6 h-6 text-amber-400" />Controlli admin
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Button data-testid="admin-cancel-bid" onClick={cancelBid} disabled={room.auction?.status !== "live" || room.auction_mode === "secret"}
                      variant="outline" className="justify-start border-white/10 hover:border-amber-500/40 h-auto py-3">
                <Ban className="w-4 h-4 mr-2 text-amber-400 flex-shrink-0" />
                <div className="text-left">
                  <div className="text-sm font-bold">Annulla ultima offerta</div>
                  <div className="text-[10px] text-slate-400">Torna all'offerta precedente</div>
                </div>
              </Button>
              <Button data-testid="admin-cancel-auction" onClick={cancelAuction} disabled={room.auction?.status !== "live"}
                      variant="outline" className="justify-start border-white/10 hover:border-red-500/40 h-auto py-3">
                <X className="w-4 h-4 mr-2 text-red-400 flex-shrink-0" />
                <div className="text-left">
                  <div className="text-sm font-bold">Annulla asta in corso</div>
                  <div className="text-[10px] text-slate-400">Nessuna aggiudicazione</div>
                </div>
              </Button>
              <Button data-testid="admin-undo-last" onClick={undoLast}
                      variant="outline" className="justify-start border-white/10 hover:border-amber-500/40 h-auto py-3">
                <RotateCcw className="w-4 h-4 mr-2 text-amber-400 flex-shrink-0" />
                <div className="text-left">
                  <div className="text-sm font-bold">Annulla ultima aggiudicazione</div>
                  <div className="text-[10px] text-slate-400">Ripristina crediti + rimuove calciatore</div>
                </div>
              </Button>
              <Button data-testid="admin-roster-btn" onClick={() => setRosterOpen(true)}
                      variant="outline" className="justify-start border-white/10 hover:border-emerald-500/40 h-auto py-3">
                <PencilLine className="w-4 h-4 mr-2 text-emerald-400 flex-shrink-0" />
                <div className="text-left">
                  <div className="text-sm font-bold">Modifica rose e crediti</div>
                  <div className="text-[10px] text-slate-400">Aggiungi/rimuovi/prezzo</div>
                </div>
              </Button>
            </div>
            <div className="pt-3 border-t border-white/10">
              {room.phase === "finished" ? (
                <div className="flex gap-2 flex-wrap">
                  <Button data-testid="admin-reopen" onClick={reopen} variant="outline" className="border-emerald-500/30">
                    Riapri lega
                  </Button>
                  <Button data-testid="export-pdf-btn" onClick={() => downloadExport("pdf")} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold">
                    <FileText className="w-4 h-4 mr-2" />Scarica PDF
                  </Button>
                  <Button data-testid="export-csv-btn" onClick={() => downloadExport("csv")} variant="outline" className="border-emerald-500/30">
                    <Download className="w-4 h-4 mr-2" />Scarica CSV
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2 flex-wrap items-center">
                  <Button data-testid="admin-finish" onClick={finishLeague} className="bg-red-500 hover:bg-red-400 text-white font-bold">
                    <PowerOff className="w-4 h-4 mr-2" />Chiudi lega ed esporta
                  </Button>
                  <span className="text-xs text-slate-400">Dopo la chiusura potrai scaricare PDF/CSV con tutte le rose.</span>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Roster management modal */}
      <Dialog open={rosterOpen} onOpenChange={setRosterOpen}>
        <DialogContent className="max-w-4xl bg-slate-950 border-white/10 max-h-[85vh] overflow-y-auto scroll-thin">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black">Rose e crediti — modifica manuale</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {room.teams.map((t) => (
              <div key={t.id} className="rounded-xl border border-white/10 bg-slate-900/40 p-4" data-testid={`admin-team-${t.id}`}>
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <div className="flex-1">
                    <div className="font-black">{t.name}</div>
                    <div className="text-xs text-slate-400">{t.user_name || "Nessun manager collegato"}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-slate-400">Crediti</Label>
                    <Input type="number" defaultValue={t.credits} onBlur={(e) => Number(e.target.value) !== Number(t.credits) && editCredits(t.id, e.target.value)}
                           className="w-24 bg-slate-900/60 border-white/10 h-9 font-mono text-right"
                           data-testid={`admin-credits-${t.id}`} />
                    <span className="text-xs text-slate-500">FM</span>
                  </div>
                  <Button size="sm" onClick={() => { setSelectedTeamId(t.id); setAddOpen(true); }}
                          className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold" data-testid={`admin-add-${t.id}`}>
                    <PlusCircle className="w-4 h-4 mr-1" />Aggiungi
                  </Button>
                </div>
                {(t.players || []).length === 0 ? (
                  <div className="text-sm text-slate-500 italic py-4 text-center">Nessun calciatore ancora</div>
                ) : (
                  <SortedTeamRoster
                    players={t.players}
                    teamId={t.id}
                    updatePrice={updatePrice}
                    removePlayer={removePlayer}
                  />
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add player to roster modal */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-2xl bg-slate-950 border-white/10 max-h-[85vh] overflow-y-auto scroll-thin">
          <DialogHeader>
            <DialogTitle className="text-xl font-black">
              Aggiungi calciatore a {room.teams.find(t => t.id === selectedTeamId)?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Tabs value={role} onValueChange={setRole}>
              <TabsList className="bg-slate-900/60 border border-white/10">
                {["P","D","C","A"].map((r) => (
                  <TabsTrigger key={r} value={r} className="data-[state=active]:bg-emerald-500 data-[state=active]:text-slate-950 font-black">{r}</TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3.5 text-slate-500" />
              <Input placeholder="Cerca calciatore…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-slate-900/60 border-white/10 h-11" />
            </div>
            <div className="max-h-[35vh] overflow-y-auto scroll-thin space-y-1">
              {availablePlayers.map((p) => (
                <button key={p.id} onClick={() => { setSelectedPlayer(p); setPriceInput(p.quotation); }}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all ${selectedPlayer?.id === p.id ? "border-emerald-400 bg-emerald-500/10" : "border-white/5 hover:border-emerald-500/40 bg-slate-900/40"}`}>
                  <RoleBadge role={p.role} />
                  <div className="flex-1 text-left">
                    <div className="font-bold">{p.name}</div>
                    <div className="text-xs text-slate-400">{p.team}</div>
                  </div>
                  <div className="font-mono font-bold text-slate-300">{p.quotation}</div>
                </button>
              ))}
            </div>
            {selectedPlayer && (
              <div className="flex items-center gap-2 pt-3 border-t border-white/10">
                <Input type="number" min="0" value={priceInput} onChange={(e) => setPriceInput(e.target.value)}
                       className="bg-slate-900/60 border-white/10 h-11 w-32 font-mono" data-testid="admin-add-price" />
                <span className="text-sm text-slate-400">FM</span>
                <div className="flex-1" />
                <Button onClick={addPlayer} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold h-11" data-testid="admin-add-confirm">
                  Aggiungi {selectedPlayer.name}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

const ROLE_SORT = ["P", "D", "C", "A"];
function SortedTeamRoster({ players, teamId, updatePrice, removePlayer }) {
  const sorted = useMemo(
    () => [...(players || [])].sort((a, b) => ROLE_SORT.indexOf(a.role) - ROLE_SORT.indexOf(b.role)),
    [players],
  );
  return (
    <div className="space-y-1">
      {sorted.map((p) => (
        <div key={p.player_id} className="flex items-center gap-2 p-2 rounded-lg bg-slate-900/60 border border-white/5">
          <RoleBadge role={p.role} />
          <div className="flex-1">
            <div className="text-sm font-bold">{p.name}</div>
            <div className="text-[10px] text-slate-400">{p.team}</div>
          </div>
          <Input type="number" defaultValue={p.price}
                 onBlur={(e) => Number(e.target.value) !== Number(p.price) && updatePrice(teamId, p.player_id, e.target.value)}
                 className="w-20 bg-slate-900/60 border-white/10 h-8 font-mono text-right"
                 data-testid={`admin-price-${p.player_id}`} />
          <span className="text-xs text-slate-500">FM</span>
          <button onClick={() => removePlayer(teamId, p.player_id)} className="p-1.5 text-slate-500 hover:text-red-400 rounded"
                  data-testid={`admin-remove-${p.player_id}`}>
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
}

